import { z } from 'zod'

import { INSTALLATION_DATE_REGEX } from './installationDateHelpers'

export const COORDINATE_PAIR_REGEX =
  /\[\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*\]/g

export const optionalNumberSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      if (input === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(input.trim())
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    if (typeof input === 'number') {
      return input
    }

    return input // Return the input as-is for further validation if not a string
  },
  z.number({ message: 'Must be a number input' }).optional()
)

export const requiredNumberSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    return input // Return the input as-is for further validation if not a string
  },
  z.number({
    required_error: 'A number is required',
    invalid_type_error: 'Must be a number input',
  })
)

export const requiredIntSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    return input // Return the input as-is for further validation if not a string
  },
  z
    .number({
      required_error: 'A number is required',
    })
    .int({
      message: 'Must be an integer input',
    })
)

export const positiveNumberSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    return input // Return the input as-is for further validation if not a string
  },
  z
    .number({
      required_error: 'A number is required',
      invalid_type_error: 'Must be a number input',
    })
    .gt(0, { message: 'Number must be greater than 0' })
)

export const optionalDateStringWithRegex = z.preprocess((input) => {
  if (typeof input === 'string') {
    const trimmedInput = input.trim()
    if (trimmedInput === '') {
      return undefined // Convert blank strings to undefined
    }
  }
  return input // Return the input for further validation if not blank
}, z.string().regex(INSTALLATION_DATE_REGEX, 'Invalid date format, must be YYYY-MM-DD').optional()) // Validate with regex and allow undefined

export const requiredDateStringWithRegex = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined // Convert blank strings to undefined
      }
    }
    return input // Return the input for further validation if not blank
  },
  z
    .string()
    .regex(INSTALLATION_DATE_REGEX, 'Date is required, must be YYYY-MM-DD')
) // Validate with regex and allow undefined

export const optionalStringSchema = z.preprocess((input) => {
  if (typeof input === 'string') {
    const trimmedInput = input.trim()
    return trimmedInput
  } else return undefined
}, z.string().optional()) // Ensure not empty and allow undefined

export const requiredStringSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined // Convert blank strings to undefined
      }
      return trimmedInput // Return the trimmed input for further validation if not blank
    }
    return input
  },
  z
    .string({ required_error: 'A string is required' })
    .min(1, 'The string must not be empty')
) // Ensure not empty and allow undefined

export const optionalGeckoAddressSchema = z.preprocess(
  (input) => {
    if (typeof input === 'object') {
      return input
    }
    return undefined
  },
  z
    .object({
      address_line_1: z.string(),
      address_line_2: z.string().optional(),
      city: z.string(),
      region: z.string(),
      country: z.string(),
      postal_code: z.string(),
    })
    .optional()
)

export const requiredCoordinatesArraySchema = z
  .string({
    required_error: 'A string of coordinates is required',
    invalid_type_error: 'Input must be a string',
  })
  .superRefine((input, context) => {
    // Match and extract all the coordinate pairs using regex
    const matches = input.match(COORDINATE_PAIR_REGEX)

    if (!matches) {
      // If no matches, the format is incorrect
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid coordinate pair format, must be [x, y], [x, y], ...',
      })
      return
    }

    if (matches.length < 3) {
      // If not enough coordinate pairs
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Must contain at least 3 coordinate pairs',
      })
    }
  })

export const requiredDropdown = z.preprocess(
  (input) => {
    return input
  },
  z.string().array().nonempty({ message: 'An option is required' })
)

export const optionalDropdown = z.preprocess((input) => {
  return input
}, z.string().array().optional())

export const requiredConeAngle = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber
      }
    }
    return input
  },
  z
    .number({
      required_error: 'A number is required',
      invalid_type_error: 'Must be a number input',
    })
    .refine((num) => num > 0 && num < 90, {
      message: 'Angle must be greater than 0 and less than 90',
    })
)
